// Entry point: History API path router + view dispatch.
//
// Three views:
//   /                  briefing index (or login prompt if no session)
//   /runs/:runId       briefing viewer
//   /enroll/:ticketId  passkey enrollment after operator-issued ticket
//
// Cloudflare Pages serves index.html for unknown paths by default, so
// fresh navigations like /enroll/<ticket> bootstrap the SPA correctly.

import {
  ApiError,
  apiPathPrefix,
  fetchMyProjects,
  getActiveProject,
  inPreviewMode,
  setActiveProject,
  wireDownloadControl,
  type MyProjectsResponse,
} from "./api";
import {
  enrollWithTicket,
  loginWithPasskey,
  logout,
  recallEmail,
} from "./auth";
import {
  fetchBriefing,
  fetchBriefingIndex,
  fetchMyFeedback,
  indexFeedbackByTarget,
  renderBriefingIndex,
  renderBriefingViewer,
  renderTocV4,
  sendReadReceipt,
  type BriefingRecord,
  type PhaseValue,
  type SubmittedFeedbackEvent,
  type TocCategory,
} from "./briefings";
import {
  applyFeedbackEventToPayload,
  type OptimisticPayload,
} from "./optimistic_feedback";
import {
  clearSessionEvents,
  registerSessionEvents,
} from "./session_events";
import {
  fetchClosedItems,
  renderClosedItems,
} from "./closed_items";
import {
  fetchHoursEntriesMe,
  fetchHoursLog,
  fetchHoursLogGenerations,
  HoursEntryController,
  renderHoursLog,
  renderHoursLogRail,
  type WeekScope,
} from "./hours_log";
import {
  fetchKilGenerations,
  fetchKilMarks,
  renderKilMarks,
} from "./kil_marks";
import {
  createSyncKilStatusHeader,
  startSyncKilPoll,
  stopActiveSyncKilPoll,
  triggerSyncKil,
} from "./sync_kil";
import {
  fetchFixtureBriefing,
  fetchFixtureBriefingIndex,
  fetchFixtureClosedItems,
  fetchFixtureHoursEntriesMe,
  fetchFixtureHoursLog,
  fetchFixtureHoursLogGenerations,
  fetchFixtureKilGenerations,
  fetchFixtureKilMarks,
  inFixtureMode,
} from "./fixture";

const FIXTURE = inFixtureMode();
const fetchIndex = FIXTURE ? fetchFixtureBriefingIndex : fetchBriefingIndex;
const fetchOne = FIXTURE ? fetchFixtureBriefing : fetchBriefing;
const fetchFeedback: (runId: string) => Promise<SubmittedFeedbackEvent[]> = FIXTURE
  ? async () => []
  : fetchMyFeedback;
const sendReceipt = FIXTURE
  ? async (_runId: string) => undefined
  : sendReadReceipt;
const fetchClosed = FIXTURE ? fetchFixtureClosedItems : fetchClosedItems;
const fetchHours = FIXTURE ? fetchFixtureHoursLog : fetchHoursLog;
const fetchHoursGens = FIXTURE ? fetchFixtureHoursLogGenerations : fetchHoursLogGenerations;
const fetchHoursMe = FIXTURE ? fetchFixtureHoursEntriesMe : fetchHoursEntriesMe;
const fetchKil = FIXTURE ? fetchFixtureKilMarks : fetchKilMarks;
const fetchKilGens = FIXTURE ? fetchFixtureKilGenerations : fetchKilGenerations;

const root = document.getElementById("app")!;

// Preview mode preserves the /preview/ prefix on every internal nav so
// the operator stays in the preview surface across clicks. Callers
// always pass the prefix-free path (e.g. "/kil") and navigate() lifts
// it into "/preview/kil" when needed.
function withPrefix(path: string): string {
  const prefix = apiPathPrefix();
  if (!prefix) return path;
  // Avoid /preview/preview/... if a caller happens to pass an already-
  // prefixed path. Defensive — every internal navigate() call site
  // passes prefix-free.
  if (path === prefix || path.startsWith(prefix + "/")) return path;
  return prefix + (path.startsWith("/") ? path : "/" + path);
}

function navigate(path: string): void {
  history.pushState({}, "", withPrefix(path));
  dispatch().catch((err) => console.error(err));
}

interface RouteMatch {
  view: "index" | "run" | "enroll" | "closed-items" | "kil" | "hours-log";
  param?: string;
  next?: string;
}

function parseRoute(): RouteMatch {
  const raw = window.location.pathname || "/";
  // Strip the preview prefix before view matching so the same route
  // table works for both live and preview surfaces. The PREVIEW banner
  // + api.ts's prefixed fetches are how the rest of the app knows
  // which side it's on.
  const prefix = apiPathPrefix();
  let path = raw;
  if (prefix && (path === prefix || path.startsWith(prefix + "/"))) {
    path = path.slice(prefix.length) || "/";
  }
  const params = new URLSearchParams(window.location.search);
  const next = params.get("next") ?? undefined;

  if (path.startsWith("/runs/")) {
    return { view: "run", param: decodeURIComponent(path.slice("/runs/".length)) };
  }
  if (path.startsWith("/enroll/")) {
    return {
      view: "enroll",
      param: decodeURIComponent(path.slice("/enroll/".length)),
      next,
    };
  }
  if (path === "/closed-items" || path === "/closed-items/") {
    return { view: "closed-items" };
  }
  if (path === "/kil" || path === "/kil/") {
    return { view: "kil" };
  }
  if (path === "/hours-log" || path === "/hours-log/") {
    return { view: "hours-log" };
  }
  return { view: "index", next };
}

// ---- #94 multi-project: active-project resolution + switching ----
//
// One passkey identity spans N projects. Before any data view renders, the SPA
// must know which project to scope to (sent as X-Project-Slug). ensureActive-
// Project resolves it from /my-projects: 0 grants → no-access, 1 → auto-select,
// >1 → a picker; the header switcher lets the user change it in place. Cached
// for the page load; a 403 from a stale/revoked choice busts the cache.

// Projects the current identity may access (from /my-projects), cached for the
// page load. Populated by ensureActiveProject; consumed by the shell switcher.
let knownProjects: string[] = [];
let projectsResolved = false;

function resetProjectResolution(): void {
  projectsResolved = false;
  knownProjects = [];
}

function prettySlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// #113 follow-up: the project-scoped sub-views (Project Status Update / Hours
// Log / Closed Archive) title themselves with the ACTIVE project's FULL formal
// name — matching the briefing header — instead of a static "Project Briefings".
// The sub-views only know the slug, so the formal name is cached from the
// briefing envelope (rememberProjectName, below) when a briefing renders and
// persisted in localStorage; landing on "/" auto-opens the latest briefing, so
// it is warm before any sub-view. Fallbacks: the title-cased slug (matches the
// switcher label) if the name isn't cached yet, then the static string when
// there's no resolved project at all (login / no-access / error).
const PROJECT_NAME_LS_PREFIX = "es_portal_project_name_";
function rememberProjectName(slug: string, name: string | null | undefined): void {
  if (!slug || !name || typeof localStorage === "undefined") return;
  localStorage.setItem(PROJECT_NAME_LS_PREFIX + slug, name);
}
function recallProjectName(slug: string): string | null {
  if (!slug || typeof localStorage === "undefined") return null;
  return localStorage.getItem(PROJECT_NAME_LS_PREFIX + slug);
}
function activeProjectTitle(): string {
  const active = getActiveProject();
  if (!active) return "Project Briefings";
  return recallProjectName(active) ?? prettySlug(active);
}

// Returns true to proceed with the data view; false after rendering an
// alternative (login / no-access / picker). Skipped in fixture mode (local data,
// no Worker / grant).
async function ensureActiveProject(): Promise<boolean> {
  if (FIXTURE) return true;
  if (projectsResolved && getActiveProject()) return true;
  let mine: MyProjectsResponse;
  try {
    mine = await fetchMyProjects();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      resetProjectResolution();
      renderLogin(renderShell("Project Briefings"));
      return false;
    }
    renderError("Could not load your projects. Please try again.");
    return false;
  }
  knownProjects = mine.projects.map((p) => p.slug);
  if (knownProjects.length === 0) {
    renderNoAccess(mine.email);
    return false;
  }
  // #113: default landing = last-visited (a valid stored choice) → else the
  // first project. No blocking picker; multi-project identities switch in place
  // from the header title dropdown (buildTitleSwitcher). A stale/revoked stored
  // slug (not in knownProjects) falls through to the first project.
  const active = getActiveProject();
  if (!(active && knownProjects.includes(active))) {
    setActiveProject(knownProjects[0]!);
  }
  projectsResolved = true;
  return true;
}

function renderNoAccess(email: string): void {
  const main = renderShell("Project Briefings");
  const card = document.createElement("section");
  card.className = "card";
  const h2 = document.createElement("h2");
  h2.textContent = "No project access yet";
  card.appendChild(h2);
  const p = document.createElement("p");
  p.className = "muted";
  p.textContent =
    `You're signed in as ${email}, but you don't have access to any project `
    + "yet. Ask your operator to grant you one.";
  card.appendChild(p);
  main.appendChild(card);
}

// #113 header project switcher — the swap affordance lives ON the title (Ray:
// "a swap icon on the header project name that drops the project list"). Shown
// only when the identity has >1 project. A caret button next to the title opens
// an in-place dropdown of the identity's projects; picking one sets the active
// project + re-dispatches (no re-login). Single-project identities (and fixture
// mode) get no icon. Replaces both the old blocking full-card picker and the
// right-side <select> switcher (one affordance).
// `titleEl` (the header <h1>) becomes a second pointer target for the same
// dropdown — client 2026-07-25: the caret opened it but clicking the project
// name itself did nothing, which reads as a dead control since the name is the
// bigger, more obvious thing to click. The caret button stays the semantic /
// keyboard affordance (so no nested-interactive heading, no duplicated role);
// the title is a pointer convenience layered on top.
function buildTitleSwitcher(titleEl?: HTMLElement | null): HTMLElement | null {
  if (FIXTURE || !projectsResolved || knownProjects.length < 2) return null;
  const active = getActiveProject();
  if (!active) return null;

  const wrap = document.createElement("div");
  wrap.className = "project-switch";

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "project-switch-trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.title = "Switch project";
  // Icon-only: the current project name already sits in the h1 to its left, so
  // the trigger carries an accessible name instead of visible text.
  trigger.setAttribute(
    "aria-label",
    `Switch project (current: ${prettySlug(active)})`,
  );
  trigger.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" '
    + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" '
    + 'stroke-linejoin="round" aria-hidden="true">'
    + '<polyline points="6 9 12 15 18 9"/></svg>';

  const menu = document.createElement("ul");
  menu.className = "project-switch-menu";
  menu.setAttribute("role", "listbox");
  menu.setAttribute("aria-label", "Switch project");
  menu.hidden = true;

  // Client 2026-07-25: label the dropdown so it's self-explanatory once open.
  // role="presentation" keeps it OUT of the listbox semantics — a listbox may
  // only contain options, so screen readers get the same meaning from the
  // aria-label above rather than hearing the heading as a selectable project.
  const heading = document.createElement("li");
  heading.className = "project-switch-heading";
  heading.setAttribute("role", "presentation");
  heading.textContent = "Switch project";
  menu.appendChild(heading);

  let open = false;
  const onDocClick = (e: MouseEvent): void => {
    if (!wrap.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      close();
      trigger.focus();
    }
  };
  function openMenu(): void {
    open = true;
    menu.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
  }
  function close(): void {
    if (!open) return;
    open = false;
    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", onDocClick);
    document.removeEventListener("keydown", onKey);
  }
  const toggle = (e: Event): void => {
    // stopPropagation matters on BOTH targets: openMenu registers the
    // click-outside listener on `document` during this same event, and the h1
    // sits OUTSIDE `wrap`, so without it the menu would open and immediately
    // close itself.
    e.stopPropagation();
    if (open) close();
    else openMenu();
  };
  trigger.addEventListener("click", toggle);

  if (titleEl) {
    titleEl.classList.add("project-switch-label");
    titleEl.title = "Switch project";
    titleEl.addEventListener("click", toggle);
  }

  for (const slug of knownProjects) {
    const li = document.createElement("li");
    li.className = "project-switch-item";
    li.setAttribute("role", "option");
    if (slug === active) li.setAttribute("aria-selected", "true");
    const opt = document.createElement("button");
    opt.type = "button";
    opt.className = "btn-link project-switch-option";
    if (slug === active) opt.classList.add("is-active");
    opt.textContent = prettySlug(slug);
    opt.addEventListener("click", () => {
      if (slug !== getActiveProject()) {
        setActiveProject(slug);
        projectsResolved = true;
        navigate("/");
      } else {
        close();
      }
    });
    li.appendChild(opt);
    menu.appendChild(li);
  }

  wrap.appendChild(trigger);
  wrap.appendChild(menu);
  return wrap;
}

// Shared auth/authz error handler for the data views. Returns true if it
// rendered an alternative (caller should stop). 401 → login; 403/400 (active
// project revoked or unset) → re-resolve (picker / auto-select / no-access).
function handleAuthError(err: unknown, container: HTMLElement, next?: string): boolean {
  if (err instanceof ApiError && err.status === 401) {
    renderLogin(container, next);
    return true;
  }
  if (err instanceof ApiError && (err.status === 403 || err.status === 400)) {
    resetProjectResolution();
    void ensureActiveProject();
    return true;
  }
  return false;
}

async function dispatch(): Promise<void> {
  const route = parseRoute();
  // Enrollment is pre-session — no project context yet.
  if (route.view === "enroll") {
    return route.param
      ? renderEnroll(route.param, route.next)
      : renderError("Missing enrollment ticket.");
  }
  // Every data view needs a resolved active project first (no-op in fixture).
  if (!(await ensureActiveProject())) return;
  switch (route.view) {
    case "run":
      return route.param ? renderRun(route.param) : renderError("Missing run id.");
    case "closed-items":
      return renderClosedItemsView();
    case "hours-log":
      return renderHoursLogView();
    case "kil":
      return renderKilView();
    default:
      return renderIndex(route.next);
  }
}

async function renderKilView(): Promise<void> {
  const main = renderShell(activeProjectTitle(), "Project Status Update");
  // "← Back to briefing" link — renderKilMarks wipes its target's
  // innerHTML, so the link must live in a sibling container that
  // sits ABOVE the kil-content div. main = [back-link] + [status] + [content].
  const backLink = document.createElement("a");
  backLink.href = "/";
  backLink.className = "spa-back-link";
  backLink.textContent = "← Back to briefing";
  backLink.addEventListener("click", (e) => {
    e.preventDefault();
    navigate("/");
  });
  main.appendChild(backLink);

  // v0.4.6 sync-kil status header — appears only while a sync-kil
  // workflow_dispatch is in flight. Lives as a sibling of the
  // content div (same reason as the back-link: renderKilMarks wipes
  // its target's innerHTML).
  const statusHeader = createSyncKilStatusHeader();
  main.appendChild(statusHeader.el);

  const content = document.createElement("div");
  content.className = "kil-content";
  main.appendChild(content);

  // Fire-and-forget trigger. The Worker decides whether to dispatch
  // (pending check + debounce). If it dispatches, show the spinner
  // immediately and start polling for completion. Preview mode is
  // exempt — the Worker refuses /preview/sync-kil and we shouldn't
  // confuse the operator with a status indicator there.
  if (!inPreviewMode() && !FIXTURE) {
    void (async () => {
      const result = await triggerSyncKil();
      if (result.dispatched && result.status) {
        statusHeader.show({
          in_flight: true,
          dispatched_at: result.status.dispatched_at,
          pending_event_count: result.status.pending_event_count,
        });
        startSyncKilPoll({
          onStatus: (s) => {
            if (s.in_flight) {
              statusHeader.show(s);
            } else {
              statusHeader.hide();
              // Refetch the KIL snapshot + generations now that the
              // workflow has finished — the docx is fresh, so the
              // Generate button should be enabled if marks exist.
              fetchKil().then((freshSnap) =>
                fetchKilGens().then((freshGens) =>
                  renderKilMarks(
                    content, freshSnap, freshSnap.project_slug,
                    freshGens.generations, repaintCb,
                  ),
                ),
              ).catch(() => undefined);
            }
          },
        });
      }
      // For dispatched=false outcomes (no-pending, debounced, 503,
      // network-error) we just leave the header hidden — no surface.
    })();
  }

  // Capture the repaint closure for the in-flight refresh above. We
  // can't declare it inside the IIFE because it references content,
  // which lives in the outer scope.
  let repaintCb: () => Promise<void> = async () => undefined;

  try {
    const [snapshot, gens] = await Promise.all([fetchKil(), fetchKilGens()]);
    // Project display name for the default-filename builder. Fixture
    // mode provides a static label; real mode pulls from the index's
    // headers or environment. For v1, project_slug is good enough —
    // the operator can edit at generation time per Decision 5.9.
    const projectName = snapshot.project_slug;
    const repaint = async (): Promise<void> => {
      const [freshSnap, freshGens] = await Promise.all([fetchKil(), fetchKilGens()]);
      renderKilMarks(content, freshSnap, projectName, freshGens.generations, repaint);
    };
    repaintCb = repaint;
    renderKilMarks(content, snapshot, projectName, gens.generations, repaint);
  } catch (err) {
    if (handleAuthError(err, content)) return;
    content.appendChild(errorMessage(err));
  }

  // Stop polling on navigation away (back/forward to briefing, etc.).
  // Mirrors the existing kil-mark-changed cleanup pattern in renderRun.
  const cleanup = (): void => {
    stopActiveSyncKilPoll();
    window.removeEventListener("popstate", cleanup);
  };
  window.addEventListener("popstate", cleanup);
}

async function renderClosedItemsView(): Promise<void> {
  const main = renderShell(activeProjectTitle(), "Closed Archive");
  // Same back-to-briefing crumb pattern as renderKilView. renderClosedItems
  // also wipes its target's innerHTML, so the back-link must live in
  // a sibling above the content container.
  const backLink = document.createElement("a");
  backLink.href = "/";
  backLink.className = "spa-back-link";
  backLink.textContent = "← Back to briefing";
  backLink.addEventListener("click", (e) => {
    e.preventDefault();
    navigate("/");
  });
  main.appendChild(backLink);

  const content = document.createElement("div");
  content.className = "closed-items-content";
  main.appendChild(content);

  try {
    const snapshot = await fetchClosed();
    renderClosedItems(content, snapshot, async () => {
      // Reopen succeeded — navigate back to the index so the operator
      // can re-open the latest briefing and see the now-open Item
      // surface there. Avoids stale Closed Items list state without
      // requiring a re-fetch dance.
      navigate("/");
    });
  } catch (err) {
    if (handleAuthError(err, content)) return;
    content.appendChild(errorMessage(err));
  }
}

async function renderHoursLogView(): Promise<void> {
  const main = renderShell(activeProjectTitle(), "Hours Log");
  const backLink = document.createElement("a");
  backLink.href = "/";
  backLink.className = "spa-back-link";
  backLink.textContent = "← Back to briefing";
  backLink.addEventListener("click", (e) => {
    e.preventDefault();
    navigate("/");
  });
  main.appendChild(backLink);

  // Left rail + main content, mirroring the PSU layout. The Download Excel
  // action sits at the TOP of the left rail (operator placement, 2026-06-27).
  const layout = document.createElement("div");
  layout.className = "layout hours-log-layout";

  const leftRail = document.createElement("div");
  leftRail.className = "left-rail";
  layout.appendChild(leftRail);

  const content = document.createElement("div");
  content.className = "viewer hours-log-content";
  layout.appendChild(content);
  main.appendChild(layout);

  try {
    const [snapshot, gens, me] = await Promise.all([
      fetchHours(), fetchHoursGens(), fetchHoursMe(),
    ]);
    // Shared week-filter selection — §A reads it, and the Generate button
    // honors it so the archived .xlsx matches what's on screen.
    const scopeRef: { value: WeekScope } = { value: "all" };
    // §7 Phase-2: a roster member (server-verified via /me) gets editable
    // hours cells; everyone else gets the read-only Phase-1 view. In fixture
    // mode Save is a no-op (no Worker) so the demo still exercises the UI.
    const controller = new HoursEntryController(
      snapshot, me,
      FIXTURE
        ? async (_w, _r, _m) => ({ ok: true, updated_at: "" })
        : undefined,
    );
    renderHoursLog(content, snapshot, scopeRef, controller);
    // Left rail: Generate button at the top + Generated Logs archive below
    // (PSU pattern). Re-fetch the list after each Generate so the new
    // archive row appears immediately.
    const paintRail = (g: typeof gens.generations): void =>
      renderHoursLogRail(leftRail, g, () => scopeRef.value, async () => {
        const fresh = await fetchHoursGens();
        paintRail(fresh.generations);
      });
    paintRail(gens.generations);
  } catch (err) {
    if (handleAuthError(err, content)) return;
    content.appendChild(errorMessage(err));
  }
}

function renderShell(
  title: string | null,
  subtitle?: string,
  subtitleTooltip?: string,
  headerAction?: HTMLElement | null,
): HTMLElement {
  root.innerHTML = "";
  const email = recallEmail();
  const hasSignOut = !!email && !FIXTURE;
  const hasContent = !!title || FIXTURE || hasSignOut;

  if (hasContent) {
    const header = document.createElement("header");
    header.className = "shell-header";

    const left = document.createElement("div");
    left.className = "shell-title";
    if (__LOGO_URL__) {
      const logo = document.createElement("img");
      logo.className = "shell-logo";
      logo.src = __LOGO_URL__;
      logo.alt = __INSTANCE_NAME__ || "";
      logo.decoding = "async";
      logo.loading = "eager";
      left.appendChild(logo);
    }
    if (title) {
      const h = document.createElement("h1");
      h.textContent = title;
      left.appendChild(h);
      // #113: the project switcher is a caret dropdown immediately to the RIGHT
      // of the title (the project name), before the "Week ending" subtitle —
      // shown only for multi-project identities. Replaces the old right-side
      // <select>.
      const titleSwitcher = buildTitleSwitcher(h);
      if (titleSwitcher) left.appendChild(titleSwitcher);
      if (subtitle) {
        const sep = document.createElement("span");
        sep.className = "shell-sep";
        sep.setAttribute("aria-hidden", "true");
        sep.textContent = "·";
        left.appendChild(sep);
        const sub = document.createElement("span");
        sub.className = "shell-subtitle";
        sub.textContent = subtitle;
        if (subtitleTooltip) sub.title = subtitleTooltip;
        left.appendChild(sub);
      }
      // Slice 7 L3 feedback: per-briefing action icons (e.g. the
      // .docx download) sit immediately next to the "Week ending"
      // subtitle so the reader scans them on the header line rather
      // than as a separate row above the executive summary.
      if (headerAction) {
        left.appendChild(headerAction);
      }
    }
    header.appendChild(left);

    const right = document.createElement("div");
    right.className = "shell-actions";
    if (FIXTURE) {
      const badge = document.createElement("span");
      badge.className = "fixture-badge";
      badge.textContent = "FIXTURE";
      badge.title = "No Worker connection — submissions will not persist.";
      right.appendChild(badge);
    }
    if (inPreviewMode()) {
      // Operator-facing surface (Q4=a: any valid session works, URL by
      // obscurity). The badge signals that this content has NOT been
      // delivered to recipients — see the preview-mode design notes.
      const badge = document.createElement("span");
      badge.className = "preview-badge";
      badge.textContent = "PREVIEW";
      badge.title =
        "Operator preview surface. This briefing has NOT been delivered to recipients; the Monday cron remains the authoritative publisher.";
      right.appendChild(badge);
    }
    // v4 Tasks #10 + #11: per-project tab links — Closed Items archive
    // + Project Status Update workflow. Visible on every view so operators can
    // pivot between briefings, archive, and KIL management.
    const kilLink = document.createElement("a");
    kilLink.href = withPrefix("/kil");
    kilLink.className = "btn-link shell-nav-link";
    kilLink.textContent = "Project Status Update";
    kilLink.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/kil");
    });
    right.appendChild(kilLink);
    // Nav order (operator 2026-06-28): PSU, then Hours Log (2nd), then
    // Closed Archive (3rd).
    // #40 Hours-Log Phase-1: per-project weekly work-task draft.
    const hoursLogLink = document.createElement("a");
    hoursLogLink.href = withPrefix("/hours-log");
    hoursLogLink.className = "btn-link shell-nav-link";
    hoursLogLink.textContent = "Hours Log";
    hoursLogLink.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/hours-log");
    });
    right.appendChild(hoursLogLink);
    const closedItemsLink = document.createElement("a");
    closedItemsLink.href = withPrefix("/closed-items");
    closedItemsLink.className = "btn-link shell-nav-link";
    closedItemsLink.textContent = "Closed Archive";
    closedItemsLink.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/closed-items");
    });
    right.appendChild(closedItemsLink);
    if (hasSignOut) {
      const out = document.createElement("button");
      out.type = "button";
      out.textContent = `Sign out (${email})`;
      out.className = "btn-link";
      out.addEventListener("click", async () => {
        await logout();
        navigate("/");
      });
      right.appendChild(out);
    }
    header.appendChild(right);

    root.appendChild(header);
  }

  const main = document.createElement("main");
  root.appendChild(main);
  return main;
}

/* Slice 7 L3 feedback: replace the prominent "Download briefing
   (.docx)" text link above the executive summary with a compact
   icon next to the "Week ending" subtitle. Less visual weight,
   discoverable in the header where users expect actions. */
function buildDownloadIcon(runId: string, weekEnd: string): HTMLElement {
  const a = document.createElement("a");
  a.className = "shell-download";
  // Download through the authenticated fetch layer, NOT a bare href — a
  // bare-anchor GET can't send X-Project-Slug and 400s post-#94 (api.ts).
  a.href = "#";
  a.setAttribute("role", "button");
  a.title = "Download briefing (.docx)";
  a.setAttribute("aria-label", "Download briefing as Word document");
  wireDownloadControl(
    a,
    `/briefings/${encodeURIComponent(runId)}/download`,
    `briefing-${weekEnd}.docx`,
    (msg) => { a.title = msg; },
  );
  // 16x16 download glyph — same line-weight as the .btn-edit pencil.
  a.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
    '<polyline points="7 10 12 15 17 10"/>' +
    '<line x1="12" y1="15" x2="12" y2="3"/>' +
    "</svg>";
  return a;
}

function renderError(message: string): void {
  const main = renderShell("Project Briefings");
  const p = document.createElement("p");
  p.className = "error";
  p.textContent = message;
  main.appendChild(p);
}

async function renderIndex(next?: string): Promise<void> {
  const main = renderShell("Project Briefings");
  try {
    const briefings = await fetchIndex();
    renderBriefingIndex(main, briefings, (runId) => {
      navigate(`/runs/${encodeURIComponent(runId)}`);
    });
    if (briefings.length > 0) {
      // Auto-open the most recent unless we were redirected here mid-
      // flow OR the user has clicked away during the in-flight fetch
      // (e.g. tapped Project Status Update / Closed Items in the header).
      // Re-check window.location before navigating so we don't clobber
      // an intentional cross-tab navigation.
      const currentPath = window.location.pathname || "/";
      const prefix = apiPathPrefix();
      const indexPaths = prefix
        ? new Set([prefix, prefix + "/"])
        : new Set(["/", ""]);
      const stillOnIndex = indexPaths.has(currentPath);
      if (!next && stillOnIndex) {
        const first = briefings[0]!;
        navigate(`/runs/${encodeURIComponent(first.run_id)}`);
      }
    }
  } catch (err) {
    if (handleAuthError(err, main, next)) return;
    main.appendChild(errorMessage(err));
  }
}

function renderLogin(container: HTMLElement, next?: string): void {
  const card = document.createElement("section");
  card.className = "card";
  const h2 = document.createElement("h2");
  h2.textContent = "Sign in";
  card.appendChild(h2);

  const p = document.createElement("p");
  p.className = "muted";
  p.textContent =
    "Sign in with the passkey you registered when you enrolled.";
  card.appendChild(p);

  const form = document.createElement("form");
  const labelEl = document.createElement("label");
  labelEl.textContent = "Email";
  const input = document.createElement("input");
  input.type = "email";
  input.name = "email";
  input.required = true;
  input.value = recallEmail() ?? "";
  labelEl.appendChild(input);
  form.appendChild(labelEl);

  const btn = document.createElement("button");
  btn.type = "submit";
  btn.textContent = "Continue with passkey";
  form.appendChild(btn);

  const status = document.createElement("p");
  status.className = "form-status";
  form.appendChild(status);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    btn.disabled = true;
    status.textContent = "Waiting for passkey…";
    try {
      await loginWithPasskey(input.value.trim());
      status.textContent = "Signed in.";
      navigate(next ?? "/");
    } catch (err) {
      console.error("[portal] login failed:", err);
      btn.disabled = false;
      if (err instanceof ApiError && err.status === 404) {
        status.textContent =
          "No passkey registered for this email. Ask your operator for an enrollment link.";
      } else if (err instanceof ApiError) {
        status.textContent = `Sign-in failed (${err.status}).`;
      } else if (err instanceof Error) {
        status.textContent = `${err.name || "Error"}: ${err.message || "cancelled"}`;
      } else {
        status.textContent = "Sign-in cancelled or failed.";
      }
    }
  });

  card.appendChild(form);
  container.appendChild(card);
}

async function renderRun(runId: string): Promise<void> {
  let record: BriefingRecord;
  let briefings;
  let feedback: SubmittedFeedbackEvent[] = [];
  let kilSnapshot: { items?: Array<{ action_items?: Array<{ ai_slug?: string }> }> } | null = null;
  try {
    [record, briefings, feedback, kilSnapshot] = await Promise.all([
      fetchOne(runId),
      fetchIndex(),
      fetchFeedback(runId).catch(() => []),
      // Fetch kil-marks snapshot in parallel so we can merge marks
      // into the briefing payload on render. The briefing payload's
      // kil_marked flags are agent-emitted (last cron); the snapshot
      // is the live source updated on every mark/unmark. Without the
      // merge, kil pills set in-session via the pencil dropdown
      // disappear after the operator navigates away and returns.
      fetchKil().catch(() => null),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      const next = `/runs/${encodeURIComponent(runId)}`;
      navigate(`/?next=${encodeURIComponent(next)}`);
      return;
    }
    // #94: the active project is no longer granted (revoked) or unset →
    // re-resolve (picker / auto-select / no-access) instead of a dead error.
    if (err instanceof ApiError && (err.status === 403 || err.status === 400)) {
      resetProjectResolution();
      void ensureActiveProject();
      return;
    }
    const main = renderShell("Briefing");
    main.appendChild(errorMessage(err));
    return;
  }

  // Merge KIL marks snapshot into the briefing payload BEFORE the
  // viewer renders. #58 (PSU = Actions only): the snapshot carries
  // marked Actions only, so flip kil_marked=true on the matching
  // payload Action. This is the source-of-truth merge that makes the
  // Action PSU pills persist across navigation: any in-session marks
  // (which updated the snapshot via the Worker mutation) light up the
  // briefing rows on every fresh render of this run. Items no longer
  // carry an independent mark, so there is no Item-level flip.
  if (kilSnapshot?.items) {
    const markedActionSlugs = new Set<string>();
    for (const item of kilSnapshot.items) {
      for (const ai of item.action_items ?? []) {
        if (ai.ai_slug) markedActionSlugs.add(ai.ai_slug);
      }
    }
    const payload = record.payload as unknown as BriefingPayloadLike;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug && markedActionSlugs.has(ai.ai_slug)) {
            ai.kil_marked = true;
          }
        }
      }
    }
  }

  // Real-time feedback #1 (Part B persistence): replay this recipient's
  // own lifecycle/priority feedback onto the payload BEFORE first render.
  // Unlike KIL (which has a Worker-mutated snapshot), closes/priority
  // don't touch the published blob until the next cron, so without this
  // the optimistic dim/move would vanish on reload / navigation. The
  // fetched `feedback` (own events only, per data.ts) is the source;
  // applied in submitted_at order so close→reopen and successive
  // priority changes net correctly. Idempotent once the cron has
  // absorbed (close-on-already-closed is a no-op).
  // B7: snapshot the payload AFTER the KIL-snapshot merge but BEFORE the
  // optimistic feedback replay, so a retract can re-derive the optimistic
  // layer from a clean baseline by replaying only the *remaining* feedback.
  // JSON clone — the payload is plain data. The optimistic appliers aren't
  // per-event-invertible (work_item_close also nulls closure_candidate), so
  // replay-from-baseline is the correct way to undo one event.
  const baselinePayload = JSON.parse(JSON.stringify(record.payload));
  function rebuildOptimisticLayer(): void {
    record.payload = JSON.parse(JSON.stringify(baselinePayload));
    const ordered = [...feedback].sort((a, b) =>
      a.submitted_at < b.submitted_at ? -1 : a.submitted_at > b.submitted_at ? 1 : 0,
    );
    const sp = record.payload as unknown as OptimisticPayload;
    const kp = record.payload as unknown as BriefingPayloadLike;
    for (const ev of ordered) {
      const id = ev.target?.id;
      if (!id) continue;
      // State kinds (close/priority/merge/dismiss) + KIL kinds — each applier
      // no-ops on kinds it doesn't own, so both are safe to call per event.
      applyFeedbackEventToPayload(sp, ev.kind, id, ev.payload);
      applyKilEventToPayload(kp, ev.kind, id);
    }
  }
  {
    const replayPayload = record.payload as unknown as OptimisticPayload;
    const ordered = [...feedback].sort((a, b) =>
      a.submitted_at < b.submitted_at ? -1 : a.submitted_at > b.submitted_at ? 1 : 0,
    );
    for (const ev of ordered) {
      const id = ev.target?.id;
      if (!id) continue;
      applyFeedbackEventToPayload(replayPayload, ev.kind, id, ev.payload);
    }
  }

  const projectName = record.payload.project.name ?? record.project_slug;
  // #113 follow-up: cache the formal name so the sub-views (Hours Log / PSU /
  // Closed Archive) can title with it too — they only have the slug otherwise.
  rememberProjectName(record.project_slug, record.payload.project.name);
  // Build the download icon when the agent attached the .docx to the
  // envelope. Sits inline next to "Week ending {date}" in the header.
  const downloadAction = record.payload.docx_b64
    ? buildDownloadIcon(record.run_id, record.week_end)
    : null;
  const main = renderShell(
    projectName,
    `Week ending ${record.week_end}`,
    `${record.week_start} – ${record.week_end}`,
    downloadAction,
  );

  const layout = document.createElement("div");
  layout.className = "layout";

  // Decision 12.11 (revised 2026-05-28, client feedback follow-up):
  // briefing list + Category TOC are SEPARATE cards in a left rail,
  // not blended inside one sidebar. Each gets its own bordered card;
  // the rail holds the sticky behavior so both cards travel together
  // on scroll.
  const leftRail = document.createElement("div");
  leftRail.className = "left-rail";

  const briefingsSidebar = document.createElement("aside");
  briefingsSidebar.className = "sidebar";
  renderBriefingIndex(
    briefingsSidebar,
    briefings,
    (id) => navigate(`/runs/${encodeURIComponent(id)}`),
    runId,
  );
  leftRail.appendChild(briefingsSidebar);

  // v4 only — TOC of every visible Category in the current briefing,
  // each linking to its #category-<slug> anchor. v3 payloads skip
  // the TOC section entirely (different thread structure).
  // #37 (dogfood item 4, 2026-06-18): the TOC is rebuilt on every optimistic
  // state change (rebuildToc, called from the feedback-state-changed / retract
  // listeners) so a Move-to-Phase re-groups the TOC live, matching the body's
  // re-band — the TOC used to be built once and only refreshed on reload.
  let rebuildToc: (() => void) | null = null;
  if ((record.payload as { payload_schema_version?: number })
        .payload_schema_version === 4) {
    const tocSidebar = document.createElement("aside");
    tocSidebar.className = "sidebar toc-sidebar";
    const buildToc = (): void => {
      const payload = record.payload as unknown as {
        threads?: Array<{
          slug?: string; name?: string; display_number?: string | null;
          status?: string; phase?: PhaseValue | null;
        }>;
        unfiled?: unknown[];
        informational?: unknown[];
        informational_acknowledged?: unknown[];
      };
      const tocEntries: TocCategory[] = [];
      for (const t of payload.threads ?? []) {
        if (!t.slug || !t.name) continue;
        // Skip closed Categories in the TOC — they render dimmed in the
        // body, and the TOC is a quick-jump for live work.
        if (t.status === "closed") continue;
        tocEntries.push({
          slug: t.slug, name: t.name,
          display_number: t.display_number ?? null,
          // #37: carry the phase so renderTocV4 can group under phase headers.
          phase: t.phase ?? null,
        });
      }
      // Decision 12.15: include Needs Your Input in the TOC when the
      // section is rendered — MIRRORS the briefings.ts gate exactly. #60:
      // candidate_threads no longer render (option (a)), and informational
      // now counts (fixes the long-standing divergence where an
      // informational-only briefing rendered the section with no TOC link).
      const hasNeedsInput =
        (payload.unfiled?.length ?? 0) > 0
        || (payload.informational?.length ?? 0) > 0
        || (payload.informational_acknowledged?.length ?? 0) > 0;
      tocSidebar.innerHTML = "";
      renderTocV4(tocSidebar, tocEntries, { hasNeedsInput });
      // renderTocV4 early-returns on a fully empty TOC; attach/detach so the
      // rail matches (a live move can add the first entry or empty the list).
      if (tocSidebar.childElementCount > 0) {
        if (!tocSidebar.isConnected) leftRail.appendChild(tocSidebar);
      } else if (tocSidebar.isConnected) {
        tocSidebar.remove();
      }
    };
    buildToc();
    rebuildToc = buildToc;
  }
  layout.appendChild(leftRail);

  // renderBriefingViewer wipes its target's innerHTML, so render into a
  // dedicated child container.
  const viewer = document.createElement("div");
  viewer.className = "viewer";
  layout.appendChild(viewer);

  main.appendChild(layout);
  renderBriefingViewer(viewer, record, indexFeedbackByTarget(feedback));

  // Optimistic in-memory mutation of record.payload in response to
  // KIL events submitted via the per-row pencil forms. forms.ts
  // dispatches `kil-mark-changed` after a successful POST; we flip
  // kil_marked on the local action/item by parsing the target id,
  // then re-render the viewer. Survives in-session navigation
  // (record stays in this closure) but resets on reload — at which
  // point the Worker-side snapshot mutation (kil_mutate.ts) and the
  // next cron's payload publish converge on the same state.
  const kilListener = (ev: Event): void => {
    const detail = (ev as CustomEvent).detail as {
      kind: string;
      targetId: string;
    } | undefined;
    if (!detail) return;
    const flipped = applyKilEventToPayload(
      record.payload as unknown as BriefingPayloadLike,
      detail.kind,
      detail.targetId,
    );
    if (flipped) {
      renderBriefingViewer(viewer, record, indexFeedbackByTarget(feedback));
    }
  };
  document.addEventListener("kil-mark-changed", kilListener);
  // Real-time feedback #1 (Part A): fold a just-submitted event into the
  // SHARED `feedback` array so the pending-count + Submitted list survive
  // the next re-render (KIL toggle / compactness chevron rebuild every
  // control from this array). No re-render here — the submitting control
  // already shows the entry locally, and forcing one would tear down its
  // open popover; the push just makes it durable on the next natural
  // re-render. Deduped by event_id (future-proof vs a refetch).
  const feedbackSubmittedListener = (ev: Event): void => {
    const detail = (ev as CustomEvent).detail as {
      event_id: string;
      kind: string;
      payload: Record<string, unknown>;
      submitted_at: string;
      targetId: string;
      targetType: string;
    } | undefined;
    if (!detail) return;
    if (feedback.some((e) => e.event_id === detail.event_id)) return;
    feedback.push({
      event_id: detail.event_id,
      run_id: runId,
      project_slug: record.project_slug,
      kind: detail.kind,
      target: { type: detail.targetType, id: detail.targetId },
      payload: detail.payload,
      submitted_at: detail.submitted_at,
      submitted_by: recallEmail() ?? "",
      via: "portal",
    });
  };
  document.addEventListener("feedback-submitted", feedbackSubmittedListener);
  // #74a: expose this run's live feedback log (the same array the listener
  // above pushes into) so a gated submit in forms.ts can hand the Worker the
  // recipient's just-POSTed move-out — before Cloudflare KV's eventually-
  // consistent list() indexes it — and the close-gate stops counting an
  // already-moved-out Item as still-open. Reference, not a copy: push/splice
  // mutate in place, so the hint always reflects the latest.
  registerSessionEvents(runId, feedback);
  // Real-time feedback #1 (Part B): apply an optimistic lifecycle/priority
  // mutation to the in-memory payload and re-render (mirrors kilListener).
  const feedbackStateListener = (ev: Event): void => {
    const detail = (ev as CustomEvent).detail as {
      kind: string;
      targetId: string;
      payload: Record<string, unknown>;
    } | undefined;
    if (!detail) return;
    const mutated = applyFeedbackEventToPayload(
      record.payload as unknown as OptimisticPayload,
      detail.kind,
      detail.targetId,
      detail.payload,
    );
    if (mutated) {
      renderBriefingViewer(viewer, record, indexFeedbackByTarget(feedback));
      // #37: a category_move_to_phase (or any state change) may re-group the
      // TOC — keep it in sync with the freshly re-banded body.
      rebuildToc?.();
    }
  };
  document.addEventListener("feedback-state-changed", feedbackStateListener);
  // B7: a Retract removed a pending event from the Worker. Drop it from the
  // shared `feedback` array, then re-derive the optimistic layer from the
  // baseline + remaining feedback and re-render — so any close/priority/merge/
  // KIL visual the event caused reverts. Non-state kinds (moves/adds/renames)
  // just leave the Submitted list, which the re-render reflects.
  const feedbackRetractedListener = (ev: Event): void => {
    const detail = (ev as CustomEvent).detail as {
      event_id: string;
    } | undefined;
    if (!detail) return;
    const idx = feedback.findIndex((e) => e.event_id === detail.event_id);
    if (idx === -1) return;
    feedback.splice(idx, 1);
    rebuildOptimisticLayer();
    renderBriefingViewer(viewer, record, indexFeedbackByTarget(feedback));
    // #37: retracting a phase move reverts the body's band — re-sync the TOC.
    rebuildToc?.();
  };
  document.addEventListener("feedback-retracted", feedbackRetractedListener);
  // Compactness toggle dispatches `rerender-briefing` after writing
  // a per-item override (or clearing one). Re-render so the chevron
  // state + content visibility flip from the new effective values.
  const rerenderListener = (): void => {
    renderBriefingViewer(viewer, record, indexFeedbackByTarget(feedback));
  };
  document.addEventListener("rerender-briefing", rerenderListener);
  // Cleanup: when the operator navigates away from this run, drop the
  // listeners so they don't leak / fire against a stale record.
  // popstate fires on every back/forward + programmatic nav.
  const cleanup = (): void => {
    clearSessionEvents();  // #74a: drop the run's gate-hint reference on nav-away.
    document.removeEventListener("kil-mark-changed", kilListener);
    document.removeEventListener("feedback-submitted", feedbackSubmittedListener);
    document.removeEventListener("feedback-state-changed", feedbackStateListener);
    document.removeEventListener("feedback-retracted", feedbackRetractedListener);
    document.removeEventListener("rerender-briefing", rerenderListener);
    window.removeEventListener("popstate", cleanup);
  };
  window.addEventListener("popstate", cleanup);

  sendReceipt(runId).catch(() => undefined);
}

// Mirror of the Worker target parsing (worker/src/kil_mutate.ts).
// Returns true if anything mutated; false on parse failure or miss.
interface BriefingPayloadLike {
  threads?: Array<{
    slug?: string;
    work_items?: Array<{
      slug?: string;
      kil_marked?: boolean;
      action_items?: Array<{
        ai_slug?: string;
        kil_marked?: boolean;
      }>;
    }>;
  }>;
}
function applyKilEventToPayload(
  payload: BriefingPayloadLike,
  kind: string,
  targetId: string,
): boolean {
  const itemMatch = targetId.match(/^thread:([^/]+)\/work_item:([^/]+)$/);
  const actionMatch = targetId.match(
    /^thread:([^/]+)\/work_item:([^/]+)\/action_item:([^/]+)$/,
  );
  if (kind === "item_kil_mark" || kind === "item_kil_unmark") {
    // #58 (PSU = Actions only): the SPA no longer offers this kind, but it
    // stays a valid wire kind (historical events still load). Replaying one
    // sets wi.kil_marked with no visible effect — the Item PSU pill was
    // removed — so this branch is inert back-compat, not a live affordance.
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.kil_marked = kind === "item_kil_mark";
        return true;
      }
    }
    return false;
  }
  if (kind === "action_kil_mark" || kind === "action_kil_unmark") {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.kil_marked = kind === "action_kil_mark";
          return true;
        }
      }
    }
    return false;
  }
  return false;
}

async function renderEnroll(ticketId: string, next?: string): Promise<void> {
  const main = renderShell("Enroll your passkey");
  const card = document.createElement("section");
  card.className = "card";

  const intro = document.createElement("p");
  intro.textContent =
    "Tap the button below and follow your device's prompt to register a passkey.";
  card.appendChild(intro);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Register passkey";
  card.appendChild(btn);

  const status = document.createElement("p");
  status.className = "form-status";
  card.appendChild(status);
  main.appendChild(card);

  btn.addEventListener("click", async () => {
    btn.disabled = true;
    status.textContent = "Waiting for device…";
    try {
      await enrollWithTicket(ticketId);
      status.textContent = "Passkey registered. Loading your briefings…";
      navigate(next ?? "/");
    } catch (err) {
      console.error("[portal] enrollment failed:", err);
      btn.disabled = false;
      if (err instanceof ApiError) {
        status.textContent = `Enrollment failed (${err.status}). Ask your operator for a fresh link.`;
      } else if (err instanceof Error) {
        status.textContent = `${err.name || "Error"}: ${err.message || "cancelled"}`;
      } else {
        status.textContent = "Enrollment cancelled or failed.";
      }
    }
  });
}

function errorMessage(err: unknown): HTMLElement {
  const p = document.createElement("p");
  p.className = "error";
  if (err instanceof ApiError) {
    p.textContent = `Error: ${err.status}`;
  } else {
    p.textContent = "Network error.";
  }
  return p;
}

window.addEventListener("popstate", () => {
  dispatch().catch((err) => console.error(err));
});
dispatch().catch((err) => console.error(err));
